import { useEffect, useRef, useState } from "react";
import { n0 } from "./format";

/* Small dependency-free SVG charts. Colours: */
// deep red first; the rest are neutral/warm so series stay distinguishable
export const SERIES = ["#8b1a1a", "#78716c", "#d98b8b", "#3a3532", "#b45309", "#c4b5ac"];

/** Line chart. labels: string[]; series: [{name, values:number[], color?}] */
export function LineChart({ labels, series, height = 240, money = true }) {
  // draw at the container's real pixel width so text stays 10-11px at any size
  const box = useRef(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    if (!box.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const H = height, P = { l: 56, r: 14, t: 12, b: 28 };
  const all = series.flatMap((s) => s.values);
  const max = Math.max(1, ...all), min = Math.min(0, ...all);
  const x = (i) => P.l + (labels.length <= 1 ? (W - P.l - P.r) / 2 : (i * (W - P.l - P.r)) / (labels.length - 1));
  const y = (v) => P.t + (1 - (v - min) / (max - min || 1)) * (H - P.t - P.b);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => min + f * (max - min));
  const every = Math.max(1, Math.ceil(labels.length / Math.max(2, Math.floor(W / 80))));
  if (!labels.length) return <div className="empty" style={{ padding: "2rem" }}>No data for this period.</div>;
  return (
    <div className="ch" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={series.map((s) => s.name).join(", ")}>
        {ticks.map((t, i) => (
          <g key={i}><line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="#eceae5" />
            <text x={P.l - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="#71717a">{money && Math.abs(t) >= 1000 ? `${n0(t / 1000)}k` : n0(t)}</text></g>
        ))}
        {labels.map((l, i) => (i % every === 0 || i === labels.length - 1) && (
          <text key={l + i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#71717a">{String(l).slice(5) || l}</text>))}
        {series.map((s, si) => {
          const c = s.color || SERIES[si % SERIES.length];
          const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
          return (
            <g key={s.name}>
              <polyline points={pts} fill="none" stroke={c} strokeWidth="2.2" strokeLinejoin="round" />
              {s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="3" fill={c}><title>{`${labels[i]} · ${s.name}: ${n0(v)}`}</title></circle>)}
            </g>
          );
        })}
      </svg>
      <Legend series={series} />
    </div>
  );
}

/** Horizontal bars. rows: [{label, values:number[]}], names: series names */
export function HBars({ rows, names, unit = "" }) {
  const max = Math.max(1, ...rows.flatMap((r) => r.values.map(Math.abs)));
  if (!rows.length) return <div className="empty" style={{ padding: "2rem" }}>No data for this period.</div>;
  return (
    <div className="ch">
      <div className="ch-hbars">
        {rows.map((r) => (
          <div key={r.label} className="ch-row">
            <span className="ch-label" title={r.label}>{r.label}</span>
            <div className="ch-bars">
              {r.values.map((v, i) => (
                <div key={i} className="ch-bar" style={{ width: `${(Math.abs(v) / max) * 100}%`, background: v < 0 ? "#ef4444" : SERIES[i % SERIES.length] }}
                  title={`${names[i]}: ${n0(v)}${unit}`} />
              ))}
            </div>
            <span className="ch-val mono">{r.values.map((v) => n0(v)).join(" / ")}</span>
          </div>
        ))}
      </div>
      {names.length > 1 && <Legend series={names.map((n) => ({ name: n }))} />}
    </div>
  );
}

function Legend({ series }) {
  return (
    <div className="ch-legend">{series.map((s, i) => <span key={s.name}><i style={{ background: s.color || SERIES[i % SERIES.length] }} />{s.name}</span>)}</div>
  );
}
